import { useEffect, useState, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Loading from '../components/Loading';
import UserProfile from '../ui/UserProfile';
import Logout from '../features/settings/logout/Logout';
import AccountDelete from '../features/settings/accountDelete/AccountDelete';
import PrivateLayout from '@/layouts/PrivateLayout';
import LanguagePreference from '../features/settings/language/LanguagePreference';
import TimeZonePreference from '../features/settings/timezone/TimeZonePreference';
import WeekStartPreference from '../features/settings/weekStart/WeekStartPreference';
import BirthYearPreference from '@/features/settings/birthYear/BirthYearPreference';
import { apiGetRequest } from '@/utils/apiClient';
import ManageSubscriptionsButton from '@/ui/ManageSubscriptionsButton';
import { isReallyBlank } from '@/utils/utils';
import { useUser } from '@/hooks/UserContext';
import { useTranslation } from 'react-i18next';

// 型定義
export interface SettingsData {
    userId: number;
    accountName: string;
    email: string;
    subscriptionStatus: string;
    googleAccountDisplayNames: string[];
    googleAccountProviderIds: string[];
    microsoftAccountDisplayNames: string[];
    microsoftAccountProviderIds: string[];
    language?: string;
    locale?: string;
    timeZone?: string;
    birthYear?: number | null;
}

/**
 * 設定画面コンポーネント
 * @returns 設定画面
 */
function Settings(): ReactElement {
    // 関数系
    const navigate = useNavigate();
    const { isLoading: userLoading } = useUser();
    const { t } = useTranslation('settings');
    const pageTitle = t('meta.title');

    // 値を扱う変数
    const [username, setUsername] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [userId, setUserId] = useState<number>(0);
    const [subscriptionStatus, setSubscriptionStatus] = useState<string>('');

    const [isLoading, setIsLoading] = useState<boolean>(true);

    // メイン処理
    useEffect(() => {
        const init = async (): Promise<void> => {
            // UserContextの読み込み中は待機
            if (userLoading) return;

            // ローディング開始
            setIsLoading(true);

            try {
                // データ取得
                const response = await apiGetRequest('/api/settings');
                // エラーの場合
                if (!response.ok) {
                    // 401未認証エラーの場合
                    if (response.status === 401) {
                        // ログインページにリダイレクト
                        window.location.replace('/login?error=unauthorized');
                        return;
                    }
                    // その他エラーの場合（サーバーエラー）
                    window.location.replace('/login?error=server_error');
                    return;
                }

                // データ取得
                const data: SettingsData = await response.json();
                // パラメータセット
                setEmail(data.email);
                setUsername(data.accountName);
                setUserId(data.userId);
                setSubscriptionStatus(data.subscriptionStatus);
            } catch (err) {
                // ログインページにリダイレクト（サーバーエラー）
                window.location.replace('/login?error=server_error');
                return;
            } finally {
                // ローディング終了
                setIsLoading(false);
            }
        };

        // 上記処理実行
        init();
    }, [navigate, userLoading]);

    // UserContextの読み込み中やSettingsのローディング中
    if (userLoading || isLoading) {
        return <Loading />;
    }

    /**
     * レンダリング
     * @returns レンダリング
     */
    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>

            <PrivateLayout>
                {/* メインコンテンツ */}
                <div className="lg:w-8/12 flex flex-col p-8 gap-2">
                    {/* ユーザー情報 */}
                    <UserProfile username={username} email={email} />

                    {/* 言語設定 */}
                    <LanguagePreference />

                    {/* タイムゾーン設定 */}
                    <TimeZonePreference />

                    {/* 週始まり設定 */}
                    <WeekStartPreference />

                    {/* 生年設定 */}
                    <BirthYearPreference />

                    {/* サブスクリプション */}
                    <h3 className="text-lg font-medium pt-5">{t('subscription.heading')}</h3>
                    <div className="flex justify-start min-h-[100px] p-4">
                        {!isReallyBlank(subscriptionStatus) ? (
                            <ManageSubscriptionsButton subscriptionStatus={subscriptionStatus} />
                        ) : (
                            <p className="text-sm text-gray-500">{t('subscription.empty')}</p>
                        )}
                    </div>

                    <hr />

                    {/* 連携サービス */}
                    {/* <ServiceConnections
            googleAccounts={googleAccounts}
            microsoftAccounts={microsoftAccounts}
          /> */}

                    <div className="flex flex-col pt-6">
                        {/* 登録情報削除 */}
                        <h3 className="text-lg font-medium">{t('accountDelete.heading')}</h3>
                        <AccountDelete userId={userId} />

                        {/* ログアウト */}
                        <h3 className="text-lg font-medium">{t('logout.heading')}</h3>
                        <Logout />
                    </div>
                </div>
            </PrivateLayout>
        </>
    );
}

export default Settings;
